import { z } from 'zod';

export const COASTER_PROJECT_SCHEMA_VERSION_V1 = 1 as const;
export const COASTER_PROJECT_SCHEMA_VERSION_V2 = 2 as const;
export const COASTER_PROJECT_SCHEMA_VERSION_V3 = 3 as const;
export const COASTER_PROJECT_SCHEMA_VERSION_V4 = 4 as const;
export const COASTER_PROJECT_SCHEMA_VERSION_V5 = 5 as const;
export const COASTER_PROJECT_SCHEMA_VERSION = 6 as const;

export const StableIdSchema = z
  .string()
  .min(1)
  .max(96)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, 'Must be a stable identifier');

const FiniteNumberSchema = z.number().finite();
const NonNegativeNumberSchema = FiniteNumberSchema.min(0);
const PositiveNumberSchema = FiniteNumberSchema.positive();
const IsoDateSchema = z.iso.datetime({ offset: true });

export const Vec3Schema = z.object({
  x: FiniteNumberSchema,
  y: FiniteNumberSchema,
  z: FiniteNumberSchema,
});

export const ColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a six-digit hex color');

const ElementReferenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('project'), id: StableIdSchema }),
  z.object({ kind: z.literal('track_node'), id: StableIdSchema }),
  z.object({ kind: z.literal('track_section'), id: StableIdSchema }),
  z.object({ kind: z.literal('support'), id: StableIdSchema }),
  z.object({ kind: z.literal('scenery'), id: StableIdSchema }),
  z.object({ kind: z.literal('highlight'), id: StableIdSchema }),
]);

export const TrackBehaviorSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('normal') }),
  z.object({
    kind: z.literal('station'),
    holdSeconds: NonNegativeNumberSchema.max(300),
    dispatchSpeedMps: NonNegativeNumberSchema.max(20),
  }),
  z.object({
    kind: z.literal('lift'),
    targetSpeedMps: PositiveNumberSchema.max(20),
  }),
  z.object({
    kind: z.literal('launch'),
    targetSpeedMps: PositiveNumberSchema.max(200),
    accelerationMps2: PositiveNumberSchema.max(100),
  }),
  z.object({
    kind: z.literal('brake'),
    targetSpeedMps: NonNegativeNumberSchema.max(200),
    decelerationMps2: PositiveNumberSchema.max(100),
  }),
  z.object({ kind: z.literal('photo') }),
]);

export const TrackNodeV1Schema = z.object({
  id: StableIdSchema,
  position: Vec3Schema,
  bankDegrees: FiniteNumberSchema.min(-180).max(180),
});

/**
 * Phase 2 stores unwrapped roll so an authored 360 degree roll does not
 * interpolate backwards when it crosses the -180/180 display boundary.
 */
export const TrackNodeV2Schema = z.object({
  id: StableIdSchema,
  position: Vec3Schema,
  bankDegrees: FiniteNumberSchema.min(-1440).max(1440),
});

/**
 * Phase 3.3 separates creator-controlled local banking from the unwrapped
 * winding required to interpolate rolls and corkscrews without reversing at
 * the -180/180 boundary.
 */
export const TrackNodeSchema = z.object({
  id: StableIdSchema,
  position: Vec3Schema,
  bankDegrees: FiniteNumberSchema.min(-90).max(90),
  rollWindingDegrees: FiniteNumberSchema.min(-1440).max(1440),
});

export const TrackSectionSchema = z.object({
  id: StableIdSchema,
  fromNodeId: StableIdSchema,
  toNodeId: StableIdSchema,
  behavior: TrackBehaviorSchema,
});

export const ModularTrackSectionGeometrySchema = z.object({
  kind: z.literal('cubic_bezier'),
  control1: Vec3Schema,
  control2: Vec3Schema,
});

export const ModularTrackSectionSchema = TrackSectionSchema.extend({
  geometry: ModularTrackSectionGeometrySchema.optional(),
});

export const TrackPieceDefinitionIdSchema = z.enum([
  'straight',
  'turn_left',
  'turn_right',
  'level_to_slope',
  'slope_to_level',
  'bank_in',
  'bank_out',
  'slope_up',
  'slope_down',
  'bank_left',
  'bank_right',
  'bank_level',
  'hill',
  'drop',
  'station',
  'lift',
  'launch',
  'brake',
  'photo',
  'loop',
  'loop_left',
  'loop_right',
  'roll_left',
  'roll_right',
  'corkscrew_left',
  'corkscrew_right',
]);

export const TrackPieceParametersSchema = z
  .object({
    length: z.enum(['short', 'medium', 'long']).optional(),
    turnDegrees: z.union([z.literal(22.5), z.literal(45), z.literal(90)]).optional(),
    slopeDegrees: z
      .union([z.literal(0), z.literal(15), z.literal(30), z.literal(45), z.literal(60)])
      .optional(),
    bank: z
      .union([
        z.literal('auto'),
        z.literal(0),
        z.literal(15),
        z.literal(30),
        z.literal(45),
        z.literal(60),
      ])
      .optional(),
    size: z.enum(['small', 'medium', 'large']).optional(),
    radius: z.enum(['small', 'medium', 'large']).optional(),
    targetPitchDegrees: z
      .union([
        z.literal(-90),
        z.literal(-60),
        z.literal(-45),
        z.literal(-30),
        z.literal(0),
        z.literal(30),
        z.literal(45),
        z.literal(60),
        z.literal(90),
      ])
      .optional(),
    targetBankDegrees: z
      .union([
        z.literal(-60),
        z.literal(-45),
        z.literal(-30),
        z.literal(0),
        z.literal(30),
        z.literal(45),
        z.literal(60),
      ])
      .optional(),
    definitionVersion: z.literal(2).optional(),
  })
  .strict();

export const TrackEndpointPoseSchema = z.object({
  position: Vec3Schema,
  headingStep: z.number().int().min(0).max(7),
  pitchDegrees: z.union([
    z.literal(-90),
    z.literal(-60),
    z.literal(-45),
    z.literal(-30),
    z.literal(0),
    z.literal(30),
    z.literal(45),
    z.literal(60),
    z.literal(90),
  ]),
  bankDegrees: z.union([
    z.literal(-60),
    z.literal(-45),
    z.literal(-30),
    z.literal(0),
    z.literal(30),
    z.literal(45),
    z.literal(60),
  ]),
  rollWindingDegrees: FiniteNumberSchema.min(-1440).max(1440),
});

export const TrackPieceInstanceSchema = z.object({
  id: StableIdSchema,
  definitionId: z.union([TrackPieceDefinitionIdSchema, z.literal('custom')]),
  sectionIds: z.array(StableIdSchema).min(1).max(256),
  parameters: TrackPieceParametersSchema.nullable(),
  definitionVersion: z.literal(2).optional(),
  entryPose: TrackEndpointPoseSchema.nullable().optional(),
  exitPose: TrackEndpointPoseSchema.nullable().optional(),
});

export const CoasterEditorDataSchema = z.object({
  pieceGroups: z.array(TrackPieceInstanceSchema).max(2_000),
});

const ValidationIssueV2Schema = z.object({
  id: StableIdSchema,
  severity: z.enum(['info', 'warning', 'severe', 'blocker']),
  code: z.string().min(1).max(80),
  message: z.string().min(1).max(500),
  element: ElementReferenceSchema,
  metric: z
    .object({
      name: z.string().min(1).max(80),
      value: FiniteNumberSchema,
      unit: z.string().min(1).max(24),
    })
    .optional(),
});

const Sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'Expected a lowercase SHA-256 hash');

export const ValidationOutcomeSchema = z.enum([
  'not_run',
  'completed',
  'stalled',
  'station_arrival_failed',
  'timeout',
  'reverse_travel',
  'numerical_failure',
  'invalid_track',
]);

export const AuthoritativeValidationIssueSchema = ValidationIssueV2Schema.extend({
  location: z
    .object({
      distanceMeters: NonNegativeNumberSchema,
      sectionId: StableIdSchema.nullable(),
      nodeId: StableIdSchema.nullable(),
    })
    .nullable(),
  carIndex: z.number().int().min(0).max(63).nullable(),
  durationSeconds: NonNegativeNumberSchema.nullable(),
  threshold: z
    .object({
      value: FiniteNumberSchema,
      unit: z.string().min(1).max(24),
      comparison: z.enum(['above', 'below', 'outside']),
    })
    .nullable(),
});

export const AuthoritativeValidationSummarySchema = z.object({
  status: z.enum(['not_run', 'valid', 'warnings', 'blocked']),
  lastRunAt: IsoDateSchema.nullable(),
  validatorVersion: z.string().min(1).max(40),
  thresholdPolicyVersion: z.string().min(1).max(40),
  trackHash: Sha256Schema.nullable(),
  outcome: ValidationOutcomeSchema,
  completionPercent: FiniteNumberSchema.min(0).max(100).nullable(),
  durationSeconds: NonNegativeNumberSchema.nullable(),
  metrics: z.object({
    topSpeedMps: NonNegativeNumberSchema.nullable(),
    maxVerticalG: FiniteNumberSchema.nullable(),
    minVerticalG: FiniteNumberSchema.nullable(),
    maxAbsLateralG: NonNegativeNumberSchema.nullable(),
    maxAbsLongitudinalG: NonNegativeNumberSchema.nullable(),
  }),
  issues: z.array(AuthoritativeValidationIssueSchema).max(1_000),
});

const ProjectMetadataSchema = z.object({
  title: z.string().min(1).max(80),
  description: z.string().max(500),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
  creator: z.object({
    kind: z.enum(['local', 'buncha']),
    id: StableIdSchema.nullable(),
    displayName: z.string().min(1).max(80),
  }),
  tags: z.array(z.string().min(1).max(32)).max(10),
});

const PlotSchema = z.object({
  shape: z.literal('rectangle'),
  sizeMeters: z.object({
    x: PositiveNumberSchema.max(2_000),
    z: PositiveNumberSchema.max(2_000),
  }),
  origin: Vec3Schema,
  groundElevationMeters: FiniteNumberSchema,
});

const TrainSchema = z.object({
  familyId: StableIdSchema,
  styleId: StableIdSchema,
  carCount: z.number().int().min(1).max(64),
  carSpacingMeters: PositiveNumberSchema.max(20),
  emptyMassKg: PositiveNumberSchema.max(1_000_000),
  capacityPerCar: z.number().int().min(1).max(16),
});

const AppearanceSchema = z.object({
  environmentThemeId: StableIdSchema,
  trackColor: ColorSchema,
  supportColor: ColorSchema,
  trainColor: ColorSchema,
  stationColor: ColorSchema,
  trackMaterialId: StableIdSchema,
  supportMaterialId: StableIdSchema,
  trainMaterialId: StableIdSchema,
});

const ManualSupportSchema = z.object({
  id: StableIdSchema,
  sectionId: StableIdSchema,
  distanceAlongSection: NonNegativeNumberSchema.max(1),
  groundAnchor: Vec3Schema,
  hidden: z.boolean(),
});

const SceneryItemSchema = z.object({
  id: StableIdSchema,
  assetId: StableIdSchema,
  position: Vec3Schema,
  rotationDegrees: Vec3Schema,
  scale: z.object({
    x: PositiveNumberSchema.max(100),
    y: PositiveNumberSchema.max(100),
    z: PositiveNumberSchema.max(100),
  }),
  colorVariant: StableIdSchema.nullable(),
});

const HighlightSchema = z.object({
  id: StableIdSchema,
  kind: z.enum(['creator', 'photo', 'automatic']),
  sectionId: StableIdSchema,
  distanceAlongSection: NonNegativeNumberSchema.max(1),
  label: z.string().min(1).max(60),
});

const HeroCameraSchema = z.object({
  position: Vec3Schema,
  target: Vec3Schema,
  verticalFovDegrees: PositiveNumberSchema.max(120),
  nearMeters: PositiveNumberSchema,
  farMeters: PositiveNumberSchema,
  safeMargin: NonNegativeNumberSchema.max(0.4),
});

const LineageSchema = z
  .object({
    sourceProjectId: StableIdSchema,
    sourceRevisionId: StableIdSchema,
    rootProjectId: StableIdSchema,
    remixedAt: IsoDateSchema,
  })
  .nullable();

const CoasterProjectCoreSchema = z.object({
  projectId: StableIdSchema,
  revisionId: StableIdSchema,
  coasterFamilyId: StableIdSchema,
  metadata: ProjectMetadataSchema,
  plot: PlotSchema,
  track: z.object({
    splineKind: z.literal('cubic_bspline'),
    degree: z.literal(3),
    closed: z.boolean(),
    nodes: z.array(TrackNodeV1Schema).max(2_000),
    sections: z.array(TrackSectionSchema).max(2_000),
  }),
  train: TrainSchema,
  appearance: AppearanceSchema,
  supports: z.object({
    mode: z.enum(['automatic', 'hidden']),
    suppressedSectionIds: z.array(StableIdSchema).max(2_000),
    manual: z.array(ManualSupportSchema).max(2_000),
  }),
  scenery: z.array(SceneryItemSchema).max(2_000),
  highlights: z.array(HighlightSchema).max(200),
  permissions: z.object({
    visibility: z.enum(['private', 'unlisted', 'public']),
    allowReadOnlyShare: z.boolean(),
    allowRemix: z.boolean(),
  }),
  validation: z.object({
    status: z.enum(['not_run', 'valid', 'warnings', 'blocked']),
    lastRunAt: IsoDateSchema.nullable(),
    validatorVersion: z.string().min(1).max(40),
    issues: z.array(ValidationIssueV2Schema).max(1_000),
  }),
  heroCamera: HeroCameraSchema,
  lineage: LineageSchema,
});

const CoasterProjectV1BaseSchema = CoasterProjectCoreSchema.extend({
  schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION_V1),
  track: z.object({
    splineKind: z.literal('cubic_bspline'),
    degree: z.literal(3),
    closed: z.boolean(),
    nodes: z.array(TrackNodeV1Schema).max(2_000),
    sections: z.array(TrackSectionSchema).max(2_000),
  }),
});

const CoasterProjectV2BaseSchema = CoasterProjectCoreSchema.extend({
  schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION_V2),
  track: z.object({
    splineKind: z.literal('cubic_bspline'),
    degree: z.literal(3),
    closed: z.boolean(),
    nodes: z.array(TrackNodeV2Schema).max(2_000),
    sections: z.array(TrackSectionSchema).max(2_000),
  }),
});

const CoasterProjectV3BaseSchema = CoasterProjectCoreSchema.extend({
  schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION_V3),
  track: z.object({
    splineKind: z.literal('cubic_bspline'),
    degree: z.literal(3),
    closed: z.boolean(),
    nodes: z.array(TrackNodeV2Schema).max(2_000),
    sections: z.array(TrackSectionSchema).max(2_000),
  }),
  validation: AuthoritativeValidationSummarySchema,
});

const CoasterProjectV4BaseSchema = CoasterProjectCoreSchema.extend({
  schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION_V4),
  track: z.object({
    splineKind: z.literal('cubic_bspline'),
    degree: z.literal(3),
    closed: z.boolean(),
    nodes: z.array(TrackNodeSchema).max(2_000),
    sections: z.array(TrackSectionSchema).max(2_000),
  }),
  validation: AuthoritativeValidationSummarySchema,
});

const CoasterProjectV5BaseSchema = CoasterProjectCoreSchema.extend({
  schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION_V5),
  track: z.object({
    splineKind: z.literal('cubic_bspline'),
    degree: z.literal(3),
    closed: z.boolean(),
    nodes: z.array(TrackNodeSchema).max(2_000),
    sections: z.array(TrackSectionSchema).max(2_000),
  }),
  validation: AuthoritativeValidationSummarySchema,
  editor: CoasterEditorDataSchema,
});

const CoasterProjectV6BaseSchema = CoasterProjectCoreSchema.extend({
  schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION),
  track: z.discriminatedUnion('geometryKind', [
    z.object({
      geometryKind: z.literal('legacy_cubic_bspline'),
      splineKind: z.literal('cubic_bspline'),
      degree: z.literal(3),
      closed: z.boolean(),
      nodes: z.array(TrackNodeSchema).max(2_000),
      sections: z.array(TrackSectionSchema).max(2_000),
    }),
    z.object({
      geometryKind: z.literal('modular_piecewise_bezier'),
      splineKind: z.literal('piecewise_bezier'),
      degree: z.literal(3),
      closed: z.boolean(),
      nodes: z.array(TrackNodeSchema).max(2_000),
      sections: z.array(ModularTrackSectionSchema).max(2_000),
    }),
  ]),
  validation: AuthoritativeValidationSummarySchema,
  editor: CoasterEditorDataSchema,
});

type ProjectRefinementValue =
  | z.infer<typeof CoasterProjectV1BaseSchema>
  | z.infer<typeof CoasterProjectV2BaseSchema>
  | z.infer<typeof CoasterProjectV3BaseSchema>
  | z.infer<typeof CoasterProjectV4BaseSchema>
  | z.infer<typeof CoasterProjectV5BaseSchema>
  | z.infer<typeof CoasterProjectV6BaseSchema>;

function refineProject(project: ProjectRefinementValue, context: z.RefinementCtx): void {
  const requireUniqueIds = (label: string, values: readonly string[], path: PropertyKey[]) => {
    const seen = new Set<string>();
    values.forEach((id, index) => {
      if (seen.has(id)) {
        context.addIssue({
          code: 'custom',
          message: `Duplicate ${label} id: ${id}`,
          path: [...path, index, 'id'],
        });
      }
      seen.add(id);
    });
  };

  requireUniqueIds(
    'track node',
    project.track.nodes.map((node) => node.id),
    ['track', 'nodes'],
  );
  requireUniqueIds(
    'track section',
    project.track.sections.map((section) => section.id),
    ['track', 'sections'],
  );
  requireUniqueIds(
    'manual support',
    project.supports.manual.map((support) => support.id),
    ['supports', 'manual'],
  );
  requireUniqueIds(
    'scenery',
    project.scenery.map((item) => item.id),
    ['scenery'],
  );
  requireUniqueIds(
    'highlight',
    project.highlights.map((item) => item.id),
    ['highlights'],
  );
  requireUniqueIds(
    'validation issue',
    project.validation.issues.map((item) => item.id),
    ['validation', 'issues'],
  );

  const nodeIds = new Set(project.track.nodes.map((node) => node.id));
  const sectionIds = new Set(project.track.sections.map((section) => section.id));
  const supportIds = new Set(project.supports.manual.map((support) => support.id));
  const sceneryIds = new Set(project.scenery.map((item) => item.id));
  const highlightIds = new Set(project.highlights.map((item) => item.id));

  project.track.sections.forEach((section, index) => {
    if (!nodeIds.has(section.fromNodeId)) {
      context.addIssue({
        code: 'custom',
        message: `Unknown track node: ${section.fromNodeId}`,
        path: ['track', 'sections', index, 'fromNodeId'],
      });
    }
    if (!nodeIds.has(section.toNodeId)) {
      context.addIssue({
        code: 'custom',
        message: `Unknown track node: ${section.toNodeId}`,
        path: ['track', 'sections', index, 'toNodeId'],
      });
    }
    if (section.fromNodeId === section.toNodeId) {
      context.addIssue({
        code: 'custom',
        message: 'A section must connect two distinct nodes',
        path: ['track', 'sections', index],
      });
    }
  });

  const expectedSectionCount = project.track.closed
    ? project.track.nodes.length
    : Math.max(0, project.track.nodes.length - 1);
  if (project.track.sections.length !== expectedSectionCount) {
    context.addIssue({
      code: 'custom',
      message: `Ordered track requires ${expectedSectionCount} sections for ${project.track.nodes.length} nodes`,
      path: ['track', 'sections'],
    });
  }
  project.track.sections.forEach((section, index) => {
    const expectedFrom = project.track.nodes[index]?.id;
    const expectedTo = project.track.closed
      ? project.track.nodes[(index + 1) % Math.max(1, project.track.nodes.length)]?.id
      : project.track.nodes[index + 1]?.id;
    if (expectedFrom && section.fromNodeId !== expectedFrom)
      context.addIssue({
        code: 'custom',
        message: 'Track sections must follow node order',
        path: ['track', 'sections', index, 'fromNodeId'],
      });
    if (expectedTo && section.toNodeId !== expectedTo)
      context.addIssue({
        code: 'custom',
        message: 'Track sections must connect neighboring nodes in order',
        path: ['track', 'sections', index, 'toNodeId'],
      });
  });

  project.supports.suppressedSectionIds.forEach((id, index) => {
    if (!sectionIds.has(id))
      context.addIssue({
        code: 'custom',
        message: `Unknown track section: ${id}`,
        path: ['supports', 'suppressedSectionIds', index],
      });
  });
  project.supports.manual.forEach((support, index) => {
    if (!sectionIds.has(support.sectionId))
      context.addIssue({
        code: 'custom',
        message: `Unknown track section: ${support.sectionId}`,
        path: ['supports', 'manual', index, 'sectionId'],
      });
  });
  project.highlights.forEach((highlight, index) => {
    if (!sectionIds.has(highlight.sectionId))
      context.addIssue({
        code: 'custom',
        message: `Unknown track section: ${highlight.sectionId}`,
        path: ['highlights', index, 'sectionId'],
      });
  });

  const referenceExists = (reference: z.infer<typeof ElementReferenceSchema>) => {
    switch (reference.kind) {
      case 'project':
        return reference.id === project.projectId;
      case 'track_node':
        return nodeIds.has(reference.id);
      case 'track_section':
        return sectionIds.has(reference.id);
      case 'support':
        return supportIds.has(reference.id);
      case 'scenery':
        return sceneryIds.has(reference.id);
      case 'highlight':
        return highlightIds.has(reference.id);
    }
  };
  project.validation.issues.forEach((issue, index) => {
    if (!referenceExists(issue.element))
      context.addIssue({
        code: 'custom',
        message: `Unknown ${issue.element.kind} reference: ${issue.element.id}`,
        path: ['validation', 'issues', index, 'element'],
      });
  });

  if (
    project.schemaVersion === COASTER_PROJECT_SCHEMA_VERSION_V5 ||
    project.schemaVersion === COASTER_PROJECT_SCHEMA_VERSION
  ) {
    const sectionOwners = new Map<string, string>();
    project.editor.pieceGroups.forEach((piece, pieceIndex) => {
      piece.sectionIds.forEach((sectionId, sectionIndex) => {
        if (!sectionIds.has(sectionId))
          context.addIssue({
            code: 'custom',
            message: `Unknown track section in piece: ${sectionId}`,
            path: ['editor', 'pieceGroups', pieceIndex, 'sectionIds', sectionIndex],
          });
        const previousOwner = sectionOwners.get(sectionId);
        if (previousOwner)
          context.addIssue({
            code: 'custom',
            message: `Track section ${sectionId} belongs to more than one piece`,
            path: ['editor', 'pieceGroups', pieceIndex, 'sectionIds', sectionIndex],
          });
        sectionOwners.set(sectionId, piece.id);
      });
    });
    project.track.sections.forEach((section, sectionIndex) => {
      if (!sectionOwners.has(section.id))
        context.addIssue({
          code: 'custom',
          message: `Track section ${section.id} is not assigned to a piece`,
          path: ['track', 'sections', sectionIndex, 'id'],
        });
    });

    project.validation.issues.forEach((issue, index) => {
      if (issue.location?.nodeId && !nodeIds.has(issue.location.nodeId))
        context.addIssue({
          code: 'custom',
          message: `Unknown track node: ${issue.location.nodeId}`,
          path: ['validation', 'issues', index, 'location', 'nodeId'],
        });
      if (issue.location?.sectionId && !sectionIds.has(issue.location.sectionId))
        context.addIssue({
          code: 'custom',
          message: `Unknown track section: ${issue.location.sectionId}`,
          path: ['validation', 'issues', index, 'location', 'sectionId'],
        });
      if (issue.carIndex !== null && issue.carIndex >= project.train.carCount)
        context.addIssue({
          code: 'custom',
          message: `Car ${issue.carIndex} does not exist in this train`,
          path: ['validation', 'issues', index, 'carIndex'],
        });
    });

    const hasRun = project.validation.outcome !== 'not_run';
    const runIdentityPresent =
      project.validation.lastRunAt !== null && project.validation.trackHash !== null;
    if (hasRun !== runIdentityPresent)
      context.addIssue({
        code: 'custom',
        message: 'Completed validation data requires a run time and matching track hash',
        path: ['validation'],
      });
    if (!hasRun && project.validation.status !== 'not_run')
      context.addIssue({
        code: 'custom',
        message: 'A validation status requires a completed validation run',
        path: ['validation', 'status'],
      });
  }

  if (
    project.schemaVersion === COASTER_PROJECT_SCHEMA_VERSION &&
    project.track.geometryKind === 'modular_piecewise_bezier'
  ) {
    project.track.sections.forEach((section, sectionIndex) => {
      if (!section.geometry)
        context.addIssue({
          code: 'custom',
          message: `Modular section ${section.id} requires cubic Bézier geometry`,
          path: ['track', 'sections', sectionIndex, 'geometry'],
        });
    });
    project.editor.pieceGroups.forEach((piece, pieceIndex) => {
      if (piece.definitionId === 'custom') return;
      if (piece.definitionVersion !== 2 || !piece.entryPose || !piece.exitPose)
        context.addIssue({
          code: 'custom',
          message: `Modular piece ${piece.id} requires versioned entry and exit poses`,
          path: ['editor', 'pieceGroups', pieceIndex],
        });
    });
  }

  if (Date.parse(project.metadata.updatedAt) < Date.parse(project.metadata.createdAt)) {
    context.addIssue({
      code: 'custom',
      message: 'updatedAt cannot precede createdAt',
      path: ['metadata', 'updatedAt'],
    });
  }
  const cameraDelta = Math.hypot(
    project.heroCamera.position.x - project.heroCamera.target.x,
    project.heroCamera.position.y - project.heroCamera.target.y,
    project.heroCamera.position.z - project.heroCamera.target.z,
  );
  if (cameraDelta < 0.001)
    context.addIssue({
      code: 'custom',
      message: 'Hero camera position and target must differ',
      path: ['heroCamera', 'target'],
    });
  if (project.heroCamera.farMeters <= project.heroCamera.nearMeters)
    context.addIssue({
      code: 'custom',
      message: 'Hero camera far plane must exceed near plane',
      path: ['heroCamera', 'farMeters'],
    });
}

export const CoasterProjectV1Schema = CoasterProjectV1BaseSchema.superRefine(refineProject);
export const CoasterProjectV2Schema = CoasterProjectV2BaseSchema.superRefine(refineProject);
export const CoasterProjectV3Schema = CoasterProjectV3BaseSchema.superRefine(refineProject);
export const CoasterProjectV4Schema = CoasterProjectV4BaseSchema.superRefine(refineProject);
export const CoasterProjectV5Schema = CoasterProjectV5BaseSchema.superRefine(refineProject);
export const CoasterProjectV6Schema = CoasterProjectV6BaseSchema.superRefine(refineProject);

export type StableId = z.infer<typeof StableIdSchema>;
export type Vec3 = z.infer<typeof Vec3Schema>;
export type TrackBehavior = z.infer<typeof TrackBehaviorSchema>;
export type TrackNode = z.infer<typeof TrackNodeSchema>;
export type TrackSection = z.infer<typeof TrackSectionSchema>;
export type CoasterProjectV1 = z.infer<typeof CoasterProjectV1Schema>;
export type CoasterProjectV2 = z.infer<typeof CoasterProjectV2Schema>;
export type CoasterProjectV3 = z.infer<typeof CoasterProjectV3Schema>;
export type CoasterProjectV4 = z.infer<typeof CoasterProjectV4Schema>;
export type CoasterProjectV5 = z.infer<typeof CoasterProjectV5Schema>;
export type CoasterProjectV6 = z.infer<typeof CoasterProjectV6Schema>;
export type CoasterProject = CoasterProjectV6;
export type TrackPieceDefinitionId = z.infer<typeof TrackPieceDefinitionIdSchema>;
export type TrackPieceParameters = z.infer<typeof TrackPieceParametersSchema>;
export type TrackPieceInstance = z.infer<typeof TrackPieceInstanceSchema>;
export type TrackEndpointPose = z.infer<typeof TrackEndpointPoseSchema>;
export type ModularTrackSectionGeometry = z.infer<typeof ModularTrackSectionGeometrySchema>;
export type AuthoritativeValidationIssue = z.infer<typeof AuthoritativeValidationIssueSchema>;
export type AuthoritativeValidationSummary = z.infer<typeof AuthoritativeValidationSummarySchema>;
export type ValidationOutcome = z.infer<typeof ValidationOutcomeSchema>;

export function createUnvalidatedValidationSummary(): AuthoritativeValidationSummary {
  return {
    status: 'not_run',
    lastRunAt: null,
    validatorVersion: 'unvalidated',
    thresholdPolicyVersion: 'unvalidated',
    trackHash: null,
    outcome: 'not_run',
    completionPercent: null,
    durationSeconds: null,
    metrics: {
      topSpeedMps: null,
      maxVerticalG: null,
      minVerticalG: null,
      maxAbsLateralG: null,
      maxAbsLongitudinalG: null,
    },
    issues: [],
  };
}

export function parseCoasterProjectV1(value: unknown): CoasterProjectV1 {
  return CoasterProjectV1Schema.parse(value);
}

export function safeParseCoasterProjectV1(value: unknown) {
  return CoasterProjectV1Schema.safeParse(value);
}

export function parseCoasterProjectV2(value: unknown): CoasterProjectV2 {
  return CoasterProjectV2Schema.parse(value);
}

export function safeParseCoasterProjectV2(value: unknown) {
  return CoasterProjectV2Schema.safeParse(value);
}

export function parseCoasterProjectV3(value: unknown): CoasterProjectV3 {
  return CoasterProjectV3Schema.parse(value);
}

export function safeParseCoasterProjectV3(value: unknown) {
  return CoasterProjectV3Schema.safeParse(value);
}

export function parseCoasterProjectV4(value: unknown): CoasterProjectV4 {
  return CoasterProjectV4Schema.parse(value);
}

export function safeParseCoasterProjectV4(value: unknown) {
  return CoasterProjectV4Schema.safeParse(value);
}

export function parseCoasterProjectV5(value: unknown): CoasterProjectV5 {
  return CoasterProjectV5Schema.parse(value);
}

export function safeParseCoasterProjectV5(value: unknown) {
  return CoasterProjectV5Schema.safeParse(value);
}

export function parseCoasterProjectV6(value: unknown): CoasterProjectV6 {
  return CoasterProjectV6Schema.parse(value);
}

export function safeParseCoasterProjectV6(value: unknown) {
  return CoasterProjectV6Schema.safeParse(value);
}

export function parseCoasterProject(value: unknown): CoasterProject {
  return CoasterProjectV6Schema.parse(value);
}

export function safeParseCoasterProject(value: unknown) {
  return CoasterProjectV6Schema.safeParse(value);
}

export function trackNodeOrientationDegrees(node: {
  readonly bankDegrees: number;
  readonly rollWindingDegrees?: number;
}): number {
  return node.bankDegrees + (node.rollWindingDegrees ?? 0);
}
